import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";
import { caseStatusOptions } from "./utils";
import { priorityOptions } from "../quality/deviations/utils";
import {
  CaseInlineSelect,
  buildAssigneeOptions,
} from "./cases-table";
import {
  InitialsTag,
  dateToString,
  NewLineFormatter,
  searchFieldsByName,
} from "../../Utils";
import { DrawerProps, DrawerSection } from "../../utils/PMTableView/RightDrawer";
import CaseUpdateComposer from "./CaseUpdateComposer";
import { fetchCaseItems, createCaseItem } from "../../../api/case";
import { UnreadEmailTagBadge } from "./UnreadEmailTagBadge";
import { useClearUnreadEmailOnOpen } from "./useMarkUnreadMailRead";
import {
  caseHasUnreadEmailTag,
  isEmailSummaryItem,
  isUnreadEmailTag,
  tagsIncludeUnreadEmail,
  unreadEmailTagLabel,
  unreadInboundMailItemIds,
  visibleCaseTagDefinitions,
} from "./unreadInboundMail";
import "./unreadInboundMail.css";

const priorityValue = (priority) => {
  if (priority == null || priority === "") return "";
  if (typeof priority === "object") {
    return String(priority?.id ?? priority?.value ?? "");
  }
  return String(priority);
};

const caseCustomerName = (item) =>
  item?.customer?.id
    ? item?.customer?.displayTitle
    : item?.fields
    ? searchFieldsByName(item, "name")?.value
    : null;

/**
 * CaseDetailDrawer body — editable status/priority/assignee, customer + type
 * info, an on-demand timeline (history) and the update composer.
 *
 * @param {object}   caseItem     — selected case row
 * @param {Array}    userOptions  — [{ value, label }] assignee options
 * @param {function} onUnreadMailCleared — (updatedCase) after Ulest svar fra kunde is cleared
 */
export const CaseDetailDrawer = ({
  caseItem,
  userOptions = [],
  onUpdate,
  onUnreadMailCleared,
}) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [openedWithUnreadMail, setOpenedWithUnreadMail] = useState(false);
  const [localCase, setLocalCase] = useState(caseItem);

  const assigneeOptions = buildAssigneeOptions(t, userOptions);
  const displayCase = localCase?.id === caseItem?.id ? localCase : caseItem;

  useEffect(() => {
    setLocalCase(caseItem);
    setOpenedWithUnreadMail(tagsIncludeUnreadEmail(caseItem));
  }, [caseItem?.id]);

  useEffect(() => {
    if (tagsIncludeUnreadEmail(caseItem)) setOpenedWithUnreadMail(true);
  }, [caseItem]);

  useClearUnreadEmailOnOpen(displayCase, {
    enabled: Boolean(displayCase?.id),
    onCleared: (updated) => {
      setLocalCase(updated);
      onUnreadMailCleared?.(updated);
    },
  });

  useEffect(() => {
    if (!caseItem?.id) return undefined;
    let active = true;
    setLoadingHistory(true);
    setHistory([]);
    fetchCaseItems(caseItem.id, { page: 1, per_page: 20, sortBy: "created_at", sort: "desc" })
      .then((res) => {
        if (!active) return;
        const items = res?.data || res || [];
        setHistory(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (active) setHistory([]);
      })
      .finally(() => {
        if (active) setLoadingHistory(false);
      });
    return () => {
      active = false;
    };
  }, [caseItem?.id]);

  const handleAddUpdate = async (payload) => {
    if (!payload || !caseItem?.id) return;
    try {
      const response = await createCaseItem(payload);
      const item = response?.data || response;
      if (item?.id) {
        toast.success(t("Update added"));
        setHistory((prev) => [
          {
            id: item.id,
            title: item.title || payload.title,
            description: payload.description,
            action: item.action || payload.action,
            module: item.module || payload.module,
            created_at: item.created_at || new Date().toISOString(),
          },
          ...prev,
        ]);
      } else {
        toast.error(item?.errors?.[0] || t("Could not add update"));
        throw new Error(item?.errors?.[0]);
      }
    } catch (err) {
      toast.error(t("Could not add update"));
      throw err;
    }
  };

  if (!caseItem) return null;

  const unreadHistoryIds = new Set(
    unreadInboundMailItemIds(history, {
      caseHasUnreadTag: caseHasUnreadEmailTag(displayCase),
      openedWithUnread: openedWithUnreadMail,
    })
  );
  const visibleHistory = history.filter((item) => !isEmailSummaryItem(item));

  return (
    <>
      {/* Quick edits */}
      <DrawerSection title={t("Details")}>
        <DrawerProps
          items={[
            {
              label: t("Status"),
              value: (
                <CaseInlineSelect
                  inline
                  options={caseStatusOptions}
                  value={caseItem.status}
                  placeholder={t("Status")}
                  onChange={(val) => onUpdate?.(caseItem, { status: val })}
                />
              ),
            },
            {
              label: t("Priority"),
              value: (
                <CaseInlineSelect
                  inline
                  options={priorityOptions}
                  value={priorityValue(caseItem.priority) || "1"}
                  placeholder={t("Priority")}
                  onChange={(val) =>
                    onUpdate?.(caseItem, { priority: val ? parseInt(val, 10) : 1 })
                  }
                />
              ),
            },
            {
              label: t("Assigned"),
              value: (
                <CaseInlineSelect
                  inline
                  options={assigneeOptions}
                  value={
                    caseItem.user_assigned?.id
                      ? String(caseItem.user_assigned.id)
                      : ""
                  }
                  placeholder={t("Ikke tildelt")}
                  renderValue={(opt) =>
                    opt.value ? (
                      <InitialsTag name={opt.label} />
                    ) : (
                      <span className="text-[13px] text-slate-400">{opt.label}</span>
                    )
                  }
                  onChange={(val) =>
                    onUpdate?.(caseItem, { user_assigned_id: val ? Number(val) : null })
                  }
                />
              ),
            },
            { label: t("Title"), value: caseItem.title || "—" },
            { label: t("Customer"), value: caseCustomerName(caseItem) || "—" },
            { label: t("Case Type"), value: caseItem.case_type?.title || "—" },
            {
              label: t("Due date"),
              value: caseItem.due_date ? dateToString(caseItem.due_date) : "—",
            },
            {
              label: t("Created"),
              value: caseItem.created_at ? dateToString(caseItem.created_at) : "—",
            },
          ]}
        />
      </DrawerSection>

      {visibleCaseTagDefinitions(displayCase).length > 0 && (
        <DrawerSection title={t("Labels")}>
          <div className="flex flex-wrap gap-1.5">
            {visibleCaseTagDefinitions(displayCase).map((tag) =>
              isUnreadEmailTag(tag) ? (
                <UnreadEmailTagBadge key={tag.id} tag={tag} />
              ) : (
                <span
                  key={tag.id}
                  className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  style={
                    tag.color
                      ? { backgroundColor: tag.color + "30", color: tag.color }
                      : { backgroundColor: "#f1f5f9", color: "#334155" }
                  }
                >
                  {unreadEmailTagLabel(tag, t) || tag.name}
                </span>
              )
            )}
          </div>
        </DrawerSection>
      )}

      {/* Add update */}
      <DrawerSection title={t("Add update")}>
        <CaseUpdateComposer
          caseId={caseItem.id}
          onSubmit={handleAddUpdate}
          compact
          customer={caseItem?.customer}
        />
      </DrawerSection>

      {/* History / latest updates */}
      <DrawerSection title={t("History")}>
        {loadingHistory ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : visibleHistory.length === 0 ? (
          <p className="text-sm italic text-slate-400">{t("No updates yet")}</p>
        ) : (
          <ol className="relative space-y-3 border-l border-slate-200 pl-4">
            {visibleHistory.map((item) => {
              const unread = unreadHistoryIds.has(item.id);
              const isEmailComm =
                item?.module === "customer_communication" &&
                item?.module_object?.channel !== "sms" &&
                item?.module_object?.channel !== "phone";
              const description = isEmailComm
                ? String(item?.module_object?.subject || "").trim()
                : String(item?.description || "").trim();
              return (
              <li key={item.id} className="relative">
                <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white ${unread ? "bg-blue-600" : "bg-blue-400"}`} />
                <div className={unread ? "pm-unread-mail-glow rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2" : "rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"}>
                  {unread ? (
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">
                      {t("case.unread_mail.item_label")}
                    </p>
                  ) : null}
                  {item.title?.trim() && (
                    <p className="mb-1 text-sm font-semibold text-slate-900">
                      {item.title}
                    </p>
                  )}
                  {description && (
                    <div className="htmlContent flex items-start gap-1.5 text-[13px] text-slate-700">
                      <ChatBubbleBottomCenterTextIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                      <div className="min-w-0 whitespace-pre-wrap break-words">
                        <NewLineFormatter value={description} />
                      </div>
                    </div>
                  )}
                  <p className="mt-1 text-[11px] tabular-nums text-slate-400">
                    {item.created_at ? dateToString(item.created_at) : ""}
                    {item.action ? ` · ${item.action}` : ""}
                  </p>
                </div>
              </li>
              );
            })}
          </ol>
        )}
      </DrawerSection>
    </>
  );
};

export default CaseDetailDrawer;
